/**
 * Plain Text Formatter - Reconstructs formatting from plain text patterns
 *
 * This module handles cases where content is pasted as plain text (common with PDFs)
 * and intelligently adds HTML formatting based on text patterns.
 */

/**
 * Analyzes plain text and converts it to formatted HTML.
 * Detects headings, lists, paragraphs, spacing, and page breaks based on patterns.
 */
export function formatPlainText(text: string): string {
    const lines = text.split('\n');
    const formattedLines: string[] = [];
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;
    let consecutiveEmptyLines = 0;
    let lastWasContent = false;

    // Pre-scan to identify repeated footers/headers (indicates page breaks)
    const repeatedLines = new Set<string>();
    const lineFrequency = new Map<string, number>();

    lines.forEach((line) => {
        const trimmed = line.trim();
        if (trimmed.length > 10 && trimmed.length < 200) {
            lineFrequency.set(trimmed, (lineFrequency.get(trimmed) || 0) + 1);
        }
    });

    // Lines that appear 3+ times are likely footers/headers
    lineFrequency.forEach((count, line) => {
        if (count >= 3) {
            repeatedLines.add(line);
        }
    });

    console.log('[PlainText] Found repeated lines (footers/headers):', Array.from(repeatedLines));

    // Track content length to estimate page breaks (approximate)
    let contentLengthSinceLastBreak = 0;
    const CHARS_PER_PAGE_ESTIMATE = 3000; // Rough estimate for letter-size page
    let linesSinceLastBreak = 0;
    const LINES_PER_PAGE_ESTIMATE = 45; // Typical lines per page

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';

        // Skip repeated footers/headers and insert page break instead
        if (repeatedLines.has(trimmed) && linesSinceLastBreak > 20) {
            if (inList) {
                formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
                inList = false;
                listType = null;
            }
            console.log('[PlainText] Detected page break at repeated line:', trimmed);
            formattedLines.push('<div data-type="page-break" class="page-break"></div>');
            contentLengthSinceLastBreak = 0;
            linesSinceLastBreak = 0;
            consecutiveEmptyLines = 0;
            continue;
        }

        // Detect explicit page break markers (common in PDF text extractions)
        if (isPageBreakMarker(trimmed)) {
            if (inList) {
                formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
                inList = false;
                listType = null;
            }
            console.log('[PlainText] Detected page break marker:', trimmed);
            formattedLines.push('<div data-type="page-break" class="page-break"></div>');
            contentLengthSinceLastBreak = 0;
            linesSinceLastBreak = 0;
            consecutiveEmptyLines = 0;
            continue;
        }

        // Handle empty lines
        if (!trimmed) {
            consecutiveEmptyLines++;

            // Close list if we're in one and hit empty lines
            if (inList && consecutiveEmptyLines >= 1) {
                formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
                inList = false;
                listType = null;
            }

            // Detect potential page breaks from excessive empty lines (8+ empty lines often indicate page break)
            if (consecutiveEmptyLines >= 8 && lastWasContent && linesSinceLastBreak > 15) {
                console.log('[PlainText] Detected page break from empty lines');
                formattedLines.push('<div data-type="page-break" class="page-break"></div>');
                contentLengthSinceLastBreak = 0;
                linesSinceLastBreak = 0;
                consecutiveEmptyLines = 0;
                lastWasContent = false;
                continue;
            }

            // Add spacing based on number of consecutive empty lines
            if (lastWasContent) {
                if (consecutiveEmptyLines === 1) {
                    // Single empty line - small gap (line break)
                    formattedLines.push('<p style="margin-top: 0.5em;"></p>');
                } else if (consecutiveEmptyLines === 2) {
                    // Double empty line - medium gap (paragraph spacing)
                    formattedLines.push('<p style="margin-top: 1em;"></p>');
                } else if (consecutiveEmptyLines >= 3 && consecutiveEmptyLines < 8) {
                    // Multiple empty lines - large gap (section break)
                    formattedLines.push('<p style="margin-top: 1.5em;"></p>');
                }
                lastWasContent = false;
            }

            continue;
        }

        consecutiveEmptyLines = 0;
        lastWasContent = true;
        linesSinceLastBreak++;

        // Track content for page break estimation
        contentLengthSinceLastBreak += trimmed.length;

        // Auto-insert page break if content is very long AND we hit a major section
        if (
            (contentLengthSinceLastBreak > CHARS_PER_PAGE_ESTIMATE ||
                linesSinceLastBreak > LINES_PER_PAGE_ESTIMATE) &&
            (isAllCapsHeading(trimmed) || trimmed.match(/^Part\s+\d+/i))
        ) {
            // Insert page break before major sections
            if (inList) {
                formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
                inList = false;
                listType = null;
            }
            console.log(
                '[PlainText] Auto-inserting page break before section:',
                trimmed.substring(0, 50)
            );
            formattedLines.push('<div data-type="page-break" class="page-break"></div>');
            contentLengthSinceLastBreak = trimmed.length;
            linesSinceLastBreak = 1;
        }

        // Detect ALL CAPS HEADINGS (like "Part 1: Our Services" or section titles)
        if (isAllCapsHeading(trimmed)) {
            if (inList) {
                formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
                inList = false;
                listType = null;
            }

            const level = getHeadingLevel(trimmed);
            // Add spacing before and after headings
            const marginTop = consecutiveEmptyLines > 0 ? '1.5em' : '1em';
            formattedLines.push(
                `<h${level} style="margin-top: ${marginTop}; margin-bottom: 0.75em;"><strong>${escapeHtml(trimmed)}</strong></h${level}>`
            );
            continue;
        }

        // Detect title-case headings (capitalized words)
        if (isTitleCaseHeading(trimmed, nextLine)) {
            if (inList) {
                formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
                inList = false;
                listType = null;
            }

            const level = getHeadingLevel(trimmed);
            const marginTop = consecutiveEmptyLines > 0 ? '1.5em' : '1em';
            formattedLines.push(
                `<h${level} style="margin-top: ${marginTop}; margin-bottom: 0.75em;"><strong>${escapeHtml(trimmed)}</strong></h${level}>`
            );
            continue;
        }

        // Detect numbered lists (1., 2., (a), (i), etc.)
        const numberedMatch = trimmed.match(/^(\d+\.|[a-z]\)|[ivx]+\)|\(\w+\))\s+(.+)$/i);
        if (numberedMatch) {
            if (!inList || listType !== 'ol') {
                if (inList) {
                    formattedLines.push('</ul>');
                }
                // Add top margin to list
                formattedLines.push('<ol style="margin-top: 0.5em; margin-bottom: 0.5em;">');
                inList = true;
                listType = 'ol';
            }

            const content = numberedMatch[2];
            formattedLines.push(`<li>${escapeHtml(content)}</li>`);
            continue;
        }

        // Detect bullet lists
        const bulletMatch = trimmed.match(/^[•\-–—●○■□▪▫◆◇★☆►▸]\s+(.+)$/);
        if (bulletMatch) {
            if (!inList || listType !== 'ul') {
                if (inList) {
                    formattedLines.push('</ol>');
                }
                formattedLines.push('<ul style="margin-top: 0.5em; margin-bottom: 0.5em;">');
                inList = true;
                listType = 'ul';
            }

            const content = bulletMatch[1];
            formattedLines.push(`<li>${escapeHtml(content)}</li>`);
            continue;
        }

        // Detect section labels like "(a)", "(b)" at start of line
        const sectionMatch = trimmed.match(/^\([a-z]\)\s+(.+)$/i);
        if (sectionMatch) {
            if (!inList || listType !== 'ol') {
                if (inList) {
                    formattedLines.push('</ul>');
                }
                formattedLines.push(
                    '<ol style="list-style-type: lower-alpha; margin-top: 0.5em; margin-bottom: 0.5em;">'
                );
                inList = true;
                listType = 'ol';
            }

            const content = sectionMatch[1];
            formattedLines.push(`<li>${escapeHtml(content)}</li>`);
            continue;
        }

        // Detect indented paragraphs (significant leading whitespace)
        const indentMatch = line.match(/^(\s{4,})(.+)$/);
        if (indentMatch && !inList) {
            const indentSize = indentMatch[1].length;
            const content = indentMatch[2];
            const indentPx = Math.floor(indentSize / 2) * 10; // Approximate indent
            formattedLines.push(
                `<p style="margin-left: ${indentPx}px;">${escapeHtml(content)}</p>`
            );
            continue;
        }

        // Close any open list if we hit regular text
        if (inList && !isListContinuation(trimmed)) {
            formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
            inList = false;
            listType = null;
        }

        // Detect bold text patterns (text between ** or text in ALL CAPS mid-sentence)
        const styledText = applyInlineStyles(trimmed);

        // Calculate paragraph spacing based on previous empty lines
        let paragraphStyle = '';
        if (consecutiveEmptyLines >= 2) {
            paragraphStyle = 'margin-top: 1em; margin-bottom: 0.5em;';
        } else if (consecutiveEmptyLines === 1) {
            paragraphStyle = 'margin-top: 0.5em; margin-bottom: 0.5em;';
        } else {
            paragraphStyle = 'margin-bottom: 0.25em;';
        }

        // Regular paragraph
        // Check if this might be a continuation of previous paragraph (no punctuation at end of previous)
        if (
            formattedLines.length > 0 &&
            !formattedLines[formattedLines.length - 1].match(/<\/(p|h\d|li|ul|ol)>$/) &&
            formattedLines[formattedLines.length - 1].match(/<p[^>]*>.*[^.!?:]$/)
        ) {
            // Continue previous paragraph
            const lastLine = formattedLines[formattedLines.length - 1];
            formattedLines[formattedLines.length - 1] = lastLine.replace(
                /<\/p>$/,
                ` ${styledText}</p>`
            );
        } else {
            formattedLines.push(`<p style="${paragraphStyle}">${styledText}</p>`);
        }
    }

    // Close any open list
    if (inList) {
        formattedLines.push(listType === 'ol' ? '</ol>' : '</ul>');
    }

    return formattedLines.join('\n');
}

/**
 * Checks if text is an ALL CAPS heading
 */
function isAllCapsHeading(text: string): boolean {
    // Must be all caps, at least 3 chars, and not just numbers/punctuation
    const alphaChars = text.replace(/[^A-Za-z]/g, '');
    if (alphaChars.length < 3) return false;

    return (
        alphaChars === alphaChars.toUpperCase() &&
        text.length < 100 && // Not too long for a heading
        !text.match(/^\d+\./)
    ); // Not a numbered list
}

/**
 * Checks if text is a Title Case heading (most words capitalized)
 */
function isTitleCaseHeading(text: string, nextLine: string): boolean {
    // Split into words
    const words = text.split(/\s+/).filter((w) => w.length > 0);

    if (words.length < 2 || words.length > 12) return false; // Reasonable heading length
    if (text.length > 100) return false; // Too long for heading
    if (text.match(/^(\d+\.|[a-z]\)|\([a-z]\))/i)) return false; // List item
    if (!text.match(/[.:]$/)) return true; // Likely heading if no ending punctuation

    // Count capitalized words (excluding small words like "a", "the", "of")
    const smallWords = ['a', 'an', 'the', 'of', 'in', 'on', 'at', 'to', 'for', 'and', 'or', 'but'];
    let capitalizedCount = 0;

    words.forEach((word, index) => {
        const alphaWord = word.replace(/[^A-Za-z]/g, '');
        if (alphaWord.length === 0) return;

        // First word should always be capitalized
        if (index === 0) {
            if (alphaWord[0] === alphaWord[0].toUpperCase()) {
                capitalizedCount++;
            }
        } else if (!smallWords.includes(alphaWord.toLowerCase())) {
            if (alphaWord[0] === alphaWord[0].toUpperCase()) {
                capitalizedCount++;
            }
        }
    });

    // At least 60% of significant words should be capitalized
    const significantWords = words.filter((w) => {
        const alpha = w.replace(/[^A-Za-z]/g, '').toLowerCase();
        return !smallWords.includes(alpha);
    });

    return capitalizedCount / Math.max(significantWords.length, 1) >= 0.6;
}

/**
 * Determines heading level based on text characteristics
 */
function getHeadingLevel(text: string): number {
    // Check for "Part X" pattern - these are major sections
    if (text.match(/^Part\s+\d+/i)) return 2;

    // Check for very short ALL CAPS (likely major heading)
    if (text.length < 30 && isAllCapsHeading(text)) return 3;

    // Check for section with colon (like "Legal Services:")
    if (text.match(/:$/)) return 3;

    // Default subsection heading
    return 4;
}

/**
 * Checks if line is likely a continuation of a list item
 */
function isListContinuation(text: string): boolean {
    // Very short lines might be list continuations
    if (text.length < 60 && !text.match(/[.!?]$/)) {
        return true;
    }
    return false;
}

/**
 * Detects page break markers in text
 * Common patterns from PDF extraction: page numbers, form feeds, footers, etc.
 */
function isPageBreakMarker(text: string): boolean {
    // Standalone page numbers at start/end (e.g., "2", "Page 2", "- 2 -", "2 of 10")
    if (text.match(/^(Page\s+)?\d+(\s+of\s+\d+)?$/i)) return true;
    if (text.match(/^-\s*\d+\s*-$/)) return true;
    if (text.match(/^\d+\s*$/)) return true; // Just a number alone

    // Form feed character
    if (text.includes('\f')) return true;

    // Horizontal separators (often used as page dividers)
    if (text.match(/^[_\-=]{3,}$/)) return true;
    if (text.match(/^[_\-=\s]{10,}$/)) return true;

    // Common PDF artifacts that indicate page boundaries
    if (text.match(/^\[Page\s+\d+\]$/i)) return true;

    // Footer patterns (copyright, page numbers with text)
    if (text.match(/^©\s*\d{4}/)) return true; // Copyright year
    if (text.match(/^\d{4}\s+[A-Z]/)) return true; // Year followed by org name

    // Trademark symbols often in footers
    if (text.match(/^®\s*\d{4}/)) return true;

    // Date patterns common in headers/footers
    if (
        text.match(
            /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}$/i
        )
    )
        return true;

    // Company/org repeated footers
    if (text.match(/^(LAWPRO|practicePRO|Professional Indemnity)/i)) return true;

    return false;
}

/**
 * Detects if a line is likely a footer/header that appears on every page
 * These often indicate page boundaries
 */
// function isRepeatedFooterOrHeader(text: string, allLines: string[], currentIndex: number): boolean {
//     const trimmed = text.trim();
//     if (trimmed.length < 10 || trimmed.length > 200) return false;

//     // Check if this exact text appears multiple times with regular spacing
//     let occurrences = 0;
//     let lastOccurrence = -1;
//     const spacing: number[] = [];

//     for (let i = 0; i < allLines.length; i++) {
//         if (allLines[i].trim() === trimmed) {
//             occurrences++;
//             if (lastOccurrence >= 0) {
//                 spacing.push(i - lastOccurrence);
//             }
//             lastOccurrence = i;
//         }
//     }

//     // If appears 3+ times with similar spacing, likely a header/footer
//     if (occurrences >= 3 && spacing.length >= 2) {
//         const avgSpacing = spacing.reduce((a, b) => a + b, 0) / spacing.length;
//         const isConsistent = spacing.every((s) => Math.abs(s - avgSpacing) < 10);
//         return isConsistent;
//     }

//     return false;
// }

/**
 * Applies inline styling (bold, italic) based on text patterns
 */
function applyInlineStyles(text: string): string {
    let result = escapeHtml(text);

    // Handle text between asterisks as bold
    result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Handle underscores as emphasis
    result = result.replace(/_(.+?)_/g, '<em>$1</em>');

    return result;
}

/**
 * Escapes HTML special characters
 */
function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Detects if pasted content is plain text (vs HTML)
 */
export function isPlainTextPaste(html: string): boolean {
    // If it's just text with line breaks and no HTML tags
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(html);

    if (!hasHtmlTags) return true;

    // Check if it's mostly plain text with minimal formatting
    const textContent = html.replace(/<[^>]+>/g, '');
    const ratio = textContent.length / html.length;

    // If >90% is text (very little HTML), treat as plain text
    return ratio > 0.9;
}

/**
 * Enhanced detection for legal document patterns
 */
export function detectLegalDocumentPatterns(text: string): {
    hasNumberedSections: boolean;
    hasPartHeaders: boolean;
    hasLetterSubsections: boolean;
} {
    return {
        hasNumberedSections: /^\d+\./m.test(text),
        hasPartHeaders: /^Part\s+\d+/im.test(text),
        hasLetterSubsections: /^\([a-z]\)/im.test(text),
    };
}
